import type { FilterAst, FilterAstCondition } from '@rammetec/data-access'

export type DataColumnType = 'string' | 'number' | 'boolean' | 'date'

export interface DataFilterCondition {
  field: string
  type: DataColumnType
  operator: string
  value?: unknown
  value2?: unknown
  values?: unknown[]
}

export interface DataFilter {
  match: 'all' | 'any'
  conditions?: DataFilterCondition[]
  /** Nested sub-filters — preserves lookup AST shape for VH compileFilter. */
  groups?: DataFilter[]
}

function mapCondition(
  child: FilterAstCondition,
  fieldType: (field: string) => DataColumnType | undefined,
): DataFilterCondition | undefined {
  if (child.field.includes('.')) return undefined

  const type = fieldType(child.field)
  if (type === undefined) return undefined

  const operator = child.op
  const condition: DataFilterCondition = {
    field: child.field,
    type,
    operator,
  }

  if (operator === 'between') {
    const values = child.values
    if (Array.isArray(values) && values.length >= 2) {
      condition.value = values[0]
      condition.value2 = values[1]
    }
  } else if (operator === 'in' || operator === 'notIn') {
    condition.values = child.values
  } else if (operator === 'isNull' || operator === 'isNotNull') {
    // no value payload
  } else {
    condition.value = child.value
  }

  return condition
}

function mapGroup(
  ast: FilterAst,
  fieldType: (field: string) => DataColumnType | undefined,
): DataFilter | undefined {
  if (ast.kind !== 'group' || !ast.children?.length) return undefined

  const conditions: DataFilterCondition[] = []
  const groups: DataFilter[] = []

  for (const child of ast.children) {
    if (child.kind === 'condition') {
      const mapped = mapCondition(child, fieldType)
      if (mapped) conditions.push(mapped)
    } else if (child.kind === 'group') {
      const nested = mapGroup(child, fieldType)
      if (nested) groups.push(nested)
    }
    // relation / nested FK filters are intentionally skipped (spec 0138)
  }

  if (conditions.length === 0 && groups.length === 0) return undefined

  return {
    match: ast.combinator === 'or' ? 'any' : 'all',
    ...(conditions.length > 0 ? { conditions } : {}),
    ...(groups.length > 0 ? { groups } : {}),
  }
}

/**
 * Converts a FilterAst tree into a VectorHub `DataFilter`.
 * Own-field `condition` nodes are kept; `relation` children are dropped.
 * Nested `group` children become `groups` so entity-lookup ASTs
 * `(search OR …) AND (active OR …)` survive the RestScript boundary.
 */
export function filterAstToDataFilter(
  ast: FilterAst | undefined,
  fieldType: (field: string) => DataColumnType | undefined,
): DataFilter | undefined {
  if (!ast) return undefined
  return mapGroup(ast, fieldType)
}
